import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Cog, Trash2, Edit, Building2, MapPin, Hash } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'operativa', label: 'Operativa', color: 'emerald' },
  { value: 'pendiente_repuesto', label: 'Esperando Repuesto', color: 'amber' },
  { value: 'en_reparacion', label: 'En Reparación', color: 'sky' },
  { value: 'fuera_servicio', label: 'Fuera de Servicio', color: 'rose' },
];

export default function AdminMachines() {
  const [machines, setMachines] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [formData, setFormData] = useState({
    company_id: '',
    name: '',
    model: '',
    serial_number: '',
    location: '',
    status: 'operativa',
    pending_parts: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [machinesRes, companiesRes] = await Promise.all([
        axios.get(`${API}/machines`),
        axios.get(`${API}/companies`)
      ]);
      setMachines(machinesRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_id || !formData.name) {
      toast.error('Empresa y nombre son requeridos');
      return;
    }

    try {
      if (editingMachine) {
        await axios.put(`${API}/machines/${editingMachine.id}`, {
          name: formData.name,
          model: formData.model,
          serial_number: formData.serial_number,
          location: formData.location,
          status: formData.status,
          pending_parts: formData.pending_parts || null
        });
        toast.success('Máquina actualizada');
      } else {
        await axios.post(`${API}/machines`, formData);
        toast.success('Máquina creada');
      }
      closeDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (machine) => {
    setEditingMachine(machine);
    setFormData({
      company_id: machine.company_id,
      name: machine.name,
      model: machine.model || '',
      serial_number: machine.serial_number || '',
      location: machine.location || '',
      status: machine.status,
      pending_parts: machine.pending_parts || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta máquina?')) return;
    try {
      await axios.delete(`${API}/machines/${id}`);
      toast.success('Máquina eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingMachine(null);
    setFormData({ company_id: '', name: '', model: '', serial_number: '', location: '', status: 'operativa', pending_parts: '' });
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    const colors = {
      emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      sky: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
      rose: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${colors[option?.color || 'emerald']}`}>
        {option?.label || status}
      </span>
    );
  };

  return (
    <Layout title="Gestión de Máquinas">
      <div className="space-y-6 animate-fade-in" data-testid="admin-machines">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-slate-400">Administra las máquinas de todas las empresas</p>
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-500 text-white gap-2" data-testid="add-machine-btn">
                <Plus className="w-4 h-4" />
                Nueva Máquina
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">
                  {editingMachine ? 'Editar Máquina' : 'Nueva Máquina'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Empresa *</Label>
                  <Select
                    value={formData.company_id || undefined}
                    onValueChange={(v) => setFormData({ ...formData, company_id: v })}
                    disabled={!!editingMachine}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="machine-company-select">
                      <SelectValue placeholder="Selecciona empresa" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Nombre *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="Torno CNC #1"
                      data-testid="machine-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Modelo</Label>
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="XYZ-2000"
                      data-testid="machine-model-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Nº Serie</Label>
                    <Input
                      value={formData.serial_number}
                      onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white font-mono"
                      placeholder="SN-12345"
                      data-testid="machine-serial-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Ubicación</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="Nave 1, Zona A"
                      data-testid="machine-location-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Estado</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="machine-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.status === 'pendiente_repuesto' && (
                  <div className="space-y-2">
                    <Label className="text-slate-300">Repuestos Pendientes</Label>
                    <Textarea
                      value={formData.pending_parts}
                      onChange={(e) => setFormData({ ...formData, pending_parts: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white min-h-[80px]"
                      placeholder="Detalla los repuestos que se están esperando..."
                      data-testid="machine-parts-input"
                    />
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={closeDialog} className="text-slate-400">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-500" data-testid="save-machine-btn">
                    {editingMachine ? 'Guardar Cambios' : 'Crear Máquina'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Machines List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : machines.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <Cog className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay máquinas registradas</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {machines.map((machine) => (
              <Card key={machine.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-rajdhani text-lg font-semibold text-white">{machine.name}</h3>
                      {machine.model && <p className="text-sm text-slate-500 font-mono">{machine.model}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(machine)} className="text-slate-400 hover:text-sky-400" data-testid={`edit-machine-${machine.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(machine.id)} className="text-slate-400 hover:text-rose-400" data-testid={`delete-machine-${machine.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <Building2 className="w-4 h-4 text-slate-500" />
                      <span>{machine.company_name}</span>
                    </div>
                    {machine.serial_number && (
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Hash className="w-4 h-4 text-slate-500" />
                        <span className="font-mono">{machine.serial_number}</span>
                      </div>
                    )}
                    {machine.location && (
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <MapPin className="w-4 h-4 text-slate-500" />
                        <span>{machine.location}</span>
                      </div>
                    )}
                  </div>
                  
                  {getStatusBadge(machine.status)}
                  
                  {machine.pending_parts && (
                    <div className="mt-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-sm">
                      <p className="text-xs text-amber-400 font-medium mb-1">Repuestos pendientes:</p>
                      <p className="text-sm text-slate-300">{machine.pending_parts}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
